import { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDisclosure } from "sprint-common-web";

import {
    isAncillaryContentDetails,
    isFullTvSeriesContentDetails,
    isMovieContentDetails,
    ContentDetails
} from "../../../../../../../../common/types/ContentDetails";
import { ContentReference } from "../../../../../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../../../../../common/types/ContentReferenceType";
import { ContentAssociationFormState } from "../../../../../../../../content-ingestion/common/content/types/ContentAssociationFormState";
import { getCloudAssetDetailsPageHref, getContentAssetsListHref } from "../../../../../../../AssetsRoutes";
import { mapContentReferenceToAssetOperationContentDetails } from "../../../../../../common/bulk-content-operations/mapContentReferenceToAssetOperationContentDetails";
import { useConformingCloudAssets } from "../../../../../common/hooks/useConformingCloudAssets";

export const useBulkEditCloudAssetContentForm = () => {
    const { assetId } = useParams<{ assetId: string }>();
    const history = useHistory();

    const { data: operations, isLoading } = useConformingCloudAssets(assetId);

    const [pendingContentReference, setPendingContentReference] = useState<ContentReference | undefined>(undefined);
    const { isOpen, onClose, onOpen } = useDisclosure();

    const currentContent = operations?.[0]?.content;

    const defaultContentState: ContentAssociationFormState = currentContent
        ? mapToContentAssociationFormState(currentContent)
        : ({} as ContentAssociationFormState);

    const onSubmit = async (contentReference?: ContentReference) => {
        if (!contentReference) return;

        setPendingContentReference(contentReference);
        onOpen();
    };

    const closeConfirmDialog = () => {
        onClose();
        setPendingContentReference(undefined);
    };

    const backToAssetsSearch = () => {
        const content: ContentDetails | undefined = pendingContentReference
            ? mapContentReferenceToAssetOperationContentDetails(pendingContentReference)
            : currentContent;

        if (!content) return;

        const href = getContentAssetsListHref(content);
        if (!href) return;

        history.push(href);
    };

    const backToAssetDetails = () => {
        const path = getCloudAssetDetailsPageHref(assetId);
        history.push(path);
    };

    return {
        assetId,
        operations,
        isLoading,
        defaultContentState,
        isDefaultContentLoading: isLoading,
        currentContent,
        pendingContentReference,
        isConfirmDialogOpen: isOpen,
        onSubmit,
        closeConfirmDialog,
        backToAssetsSearch,
        backToAssetDetails
    };
};

export const mapToContentAssociationFormState = (content: ContentDetails): ContentAssociationFormState => {
    if (isMovieContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Movie,
            contentId: content.movie.id,
            versionIds: content.versionSummary.ids
        };
    }

    if (isFullTvSeriesContentDetails(content)) {
        return {
            contentType: ContentReferenceType.TvSeries,
            contentId: content.tvSeries.id,
            episodeId: content.episode.id,
            seasonId: content.season.id,
            versionIds: content.versionSummary.ids
        };
    }

    if (isAncillaryContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Ancillary,
            contentId: content.ancillary.id,
            airlineId: content.airline.id
        };
    }

    throw new Error("Unsupported content subtype");
};
